import { Component, output, input, computed, signal, WritableSignal, effect, inject, ElementRef } from '@angular/core';

import { MDButtonGroupComponent, MDButtonToggleComponent, MDDropDownComponent, MDIconComponent } from '@melodic-ui/components';
import { ScrollToTopService } from '@lb/services';

@Component({
	selector: 'lb-paginator',
	standalone: true,
	imports: [MDDropDownComponent, MDButtonGroupComponent, MDButtonToggleComponent, MDIconComponent],
	templateUrl: './paginator.component.html',
	styleUrl: './paginator.component.scss'
})
export class PaginatorComponent {
	totalItems = input<number>(1);
	itemsPerPageOptions = input<number[]>([1, 2, 5]);
	/**
	 * Controlled page size — the single source of truth for both the size dropdown and the page
	 * count. The parent MUST write the value emitted by `pageLengthChange` back into whatever
	 * signal it binds here; a parent that binds one signal and updates a different one leaves the
	 * dropdown and `totalPages` reading a stale size (the bug this used to shadow internally).
	 */
	itemsPerPage = input<number>(5);
	maxVisible = input<number>(15);
	/** Element to scroll to when page changes. If not provided, will try to find a scrollable container */
	scrollTargetElement = input<Element | null>(null);
	/** Whether to enable auto-scroll to top on page change (default: true) */
	enableAutoScroll = input<boolean>(true);
	/** Offset from top when scrolling (default: 0) */
	scrollOffset = input<number>(0);
	pageChange = output<number>();
	pageLengthChange = output<number>();

	/**
	 * Controlled current page. When bound, the parent owns the page (e.g. mirrors PagedResponse.page)
	 * and this component simply reflects it. When left undefined, the paginator self-tracks the page
	 * internally (backward-compatible behavior for client-side lists).
	 */
	currentPage = input<number | undefined>(undefined);

	private _elementRef = inject(ElementRef);
	private _scrollService = inject(ScrollToTopService);

	private _internalPage: WritableSignal<number> = signal(1);

	/** The page actually displayed: the controlled input when provided, otherwise internal tracking. */
	public displayPage = computed(() => this.currentPage() ?? this._internalPage());

	constructor() {
		// Clamp when the current page falls past the end (e.g. after the result set shrinks).
		effect(() => {
			const totalPages = this.totalPages();

			// Ignore transient/empty load states (totalItems briefly 0) so we never snap to page 1 mid-fetch.
			if (totalPages < 1) return;

			if (this.displayPage() > totalPages) {
				// Only self-correct in uncontrolled mode; a controlled parent owns the page and already
				// resets to 1 on filter/sort/page-size changes, so we must not fight it here.
				if (this.currentPage() == null) {
					this._internalPage.set(totalPages);
					this.onPageChange(totalPages);
				}
			}
		});
	}

	totalPages = computed(() => Math.ceil(this.totalItems() / Math.max(1, this.itemsPerPage())));

	pages = computed(() => {
		const totalNumOfPage = this.totalPages();
		const currentPage = this.displayPage();

		if (totalNumOfPage <= this.maxVisible()) {
			return Array.from({ length: totalNumOfPage }, (_, i) => ({ type: 'page', value: i + 1 }));
		}

		const pages: { type: 'page' | 'ellipsis'; value?: number }[] = [];

		//Always Show First Page
		pages.push({ type: 'page', value: 1 });

		const visiblePageCount = this.maxVisible() - 2;
		let startRange: number;
		let endRange: number;
		const visiblePageEnd = Math.floor(visiblePageCount / 2);

		if (currentPage <= Math.ceil(visiblePageCount / 2)) {
			startRange = 2;
			endRange = visiblePageCount;
		} else if (currentPage >= totalNumOfPage - visiblePageEnd) {
			startRange = totalNumOfPage - visiblePageCount + 1;
			endRange = totalNumOfPage - 1;
		} else {
			startRange = currentPage - visiblePageEnd + 1;
			endRange = currentPage + visiblePageEnd - 1;
		}

		startRange = Math.max(2, startRange);
		endRange = Math.min(totalNumOfPage - 1, endRange);

		if (startRange > 2) {
			pages.push({ type: 'ellipsis', value: startRange - 1 });
		}

		for (let i = startRange; i <= endRange; i++) {
			pages.push({ type: 'page', value: i });
		}

		if (endRange < totalNumOfPage - 1) {
			pages.push({ type: 'ellipsis', value: endRange + 1 });
		}

		//Always Show Last Page
		pages.push({ type: 'page', value: totalNumOfPage });
		return pages;
	});

	itemsPerPageSelections = computed(() => {
		const options = this.itemsPerPageOptions();
		return options.map((t) => ({
			value: t,
			label: '' + t
		}));
	});

	// Change number of results per page
	onPageLengthChange(length: any): void {
		if (!length) return;

		this._internalPage.set(1);
		this.pageLengthChange.emit(length);

		if (this.enableAutoScroll()) {
			this._performAutoScroll();
		}
	}

	// Change page number
	onPageChange(page: number | undefined): void {
		if (page && page > 0 && page <= this.totalPages()) {
			this._internalPage.set(page);
			this.pageChange.emit(page);

			if (this.enableAutoScroll()) {
				this._performAutoScroll();
			}
		}
	}

	/**
	 * Scrolls to the top of the table/list when page changes
	 */
	private _performAutoScroll(): void {
		// Use a longer delay to allow the DOM to update first
		setTimeout(() => {
			const targetElement = this.scrollTargetElement();

			if (targetElement) {
				this._scrollService.scrollTableToTop(targetElement, this.scrollOffset());
			} else {
				// Try to find a table or grid element in the document
				const tableElement = this._findTableElement();
				this._scrollService.scrollTableToTop(tableElement, this.scrollOffset());
			}
		}, 100);
	}

	/**
	 * Attempts to find a table or grid element to scroll to
	 */
	private _findTableElement(): Element | null {
		// Look for common table/grid selectors near the paginator
		const paginatorElement = this._elementRef.nativeElement;
		const parent = paginatorElement.closest('div');

		if (parent) {
			// Look for tables, grids, or elements with grid/table in their class names
			const selectors = [
				'table',
				'[class*="grid"]',
				'[class*="table"]',
				'[class*="list"]',
				'.data-grid',
				'.grid-container',
				'md-data-table',
				'lb-data-grid'
			];

			for (const selector of selectors) {
				const element = parent.querySelector(selector);
				if (element) {
					return element;
				}
			}

			// Look in previous siblings for tables/grids
			let sibling = paginatorElement.parentElement?.previousElementSibling;
			while (sibling) {
				if (sibling.matches('table, [class*="grid"], [class*="table"], [class*="list"]')) {
					return sibling;
				}
				const nestedElement = sibling.querySelector('table, [class*="grid"], [class*="table"], [class*="list"]');
				if (nestedElement) {
					return nestedElement;
				}
				sibling = sibling.previousElementSibling;
			}
		}

		return null;
	}
}
